import type { Json, StatusChange, Trigger } from '@norscom/nap-shared';

import type { ReferenceValue } from 'details';

// Plain-language renderings of a trigger's configuration, for its details pane. Each
// section's collapsed summary and its expanded rows are built from these, so the two
// cannot disagree.

/** A reference's name, or its noun and id when it has none; `null` when there is no reference. */
const referenceName = (reference: ReferenceValue | null, noun: string): string | null => {
  if (reference === null) return null;
  return reference.name === null || reference.name === '' ? `${noun} #${reference.id}` : reference.name;
};

/** A reference's name with its zone, as the pane's reference rows show it. */
const nameWithZone = (reference: ReferenceValue | null, noun: string): string | null => {
  const name = referenceName(reference, noun);
  const zone = reference?.name ? reference.zone?.name : undefined;
  return name !== null && zone ? `${name} (${zone})` : name;
};

/** The watched node definition; no node definition means any node. */
export const watchedNode = (sc: StatusChange): string => referenceName(sc.node_definition, 'node definition') ?? 'Any node';

const ALL_ZONES = 'All zones';

/** The zones whose status changes fire the trigger, or null when none are recorded. */
export const monitoredZones = (trigger: Trigger): string | null => {
  if (trigger.monitor_all_zones) return ALL_ZONES;
  return trigger.monitor_zones.length > 0 ? trigger.monitor_zones.map((zone) => zone.name).join(', ') : null;
};

/** A status list's statuses; null and an empty list both mean any status (see triggerStatusField). */
const statusesOf = (statuses: readonly string[] | null): readonly string[] => statuses ?? [];

/** "failed", "running or queued", "a, b or c". */
const statusPhrase = (statuses: readonly string[]): string =>
  statuses.length === 1 ? statuses[0] : `${statuses.slice(0, -1).join(', ')} or ${statuses[statuses.length - 1]}`;

const statusChange = (sc: StatusChange): string => {
  const from = statusesOf(sc.from_status);
  const to = statusesOf(sc.to_status);
  if (from.length === 0 && to.length === 0) return 'changes status';
  if (from.length === 0) return `changes to ${statusPhrase(to)}`;
  if (to.length === 0) return `changes from ${statusPhrase(from)}`;
  return `changes from ${statusPhrase(from)} to ${statusPhrase(to)}`;
};

/** E.g. "LoadDaily changes to failed in all zones." */
export const conditionSummary = (trigger: Trigger, sc: StatusChange): string => {
  const zones = monitoredZones(trigger);
  // Zone names keep their case; only the "all zones" phrase is lowered mid-sentence.
  const where = zones === null ? '' : ` in ${zones === ALL_ZONES ? ALL_ZONES.toLowerCase() : zones}`;
  return `${watchedNode(sc)} ${statusChange(sc)}${where}.`;
};

/** Auto-restart firing applies only where the watched change can be a failure (as in the editor). */
export const restartFireModeApplies = (sc: StatusChange): boolean => {
  const to = statusesOf(sc.to_status);
  return to.length === 0 || to.includes('failed');
};

export const targetEvent = (sc: StatusChange): string | null => nameWithZone(sc.target_event, 'event');

export const targetNode = (sc: StatusChange): string | null => referenceName(sc.target_node_definition, 'node definition');

/** Where a child is submitted when the trigger names no queue: the editor's documented fallback. */
export const DEFAULT_QUEUE = "the triggering node's queue (or the target's default)";

/** The queue the child is submitted on. */
export const targetQueue = (sc: StatusChange): string => nameWithZone(sc.queue, 'queue') ?? DEFAULT_QUEUE;

/** E.g. "Raises FileArrived (ACME)." or "Submits Cleanup on the triggering node's queue." */
export const actionSummary = (sc: StatusChange): string => {
  switch (sc.action) {
    case 'raise_event':
      return `Raises ${targetEvent(sc) ?? 'an event'}.`;
    case 'submit_child':
      return `Submits ${targetNode(sc) ?? 'a node'} on ${targetQueue(sc)}.`;
    default:
      // Not checked by the database (see the shared StatusChange), and ignored by the runtime.
      return `Does nothing: "${sc.action}" is not an action.`;
  }
};

/** One parameter override of a `submit_child`. */
export interface ParameterOverride {
  readonly name: string;
  readonly value: Json;
}

/**
 * The parameter overrides, when they are stored as the editor writes them: a list of
 * `{ name, value }`, or `null` for none. Nothing validates them on write, so anything
 * else is `null`, and the pane shows the stored JSON instead.
 */
export const parameterOverrides = (parameters: Json): readonly ParameterOverride[] | null => {
  if (parameters === null) return [];
  if (!Array.isArray(parameters)) return null;
  const overrides: ParameterOverride[] = [];
  for (const parameter of parameters) {
    if (parameter === null || typeof parameter !== 'object' || Array.isArray(parameter) || typeof parameter.name !== 'string') return null;
    overrides.push({ name: parameter.name, value: parameter.value ?? null });
  }
  return overrides;
};

/** A parameter override's value as text. It is any JSON value on the wire, usually a string. */
export const parameterValue = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value));
